/**
 * Multi-step guided tour runtime, with steps that live on OTHER pages.
 *
 * A step declares where it lives as data (`path`, `to`); the runtime does the
 * routing, so no hook needs its own navigation code:
 *
 *   state          what is on screen                        leaves on
 *   -----          -----------------                        ---------
 *   showing(i)     overlay + spotlight + popover of step i  next / prev / close
 *   loading(i)     overlay + spinner popover (route change  target found -> showing
 *                  done, waiting for the target)            8 s -> fallback(i)
 *                                                           close -> ended
 *   fallback(i)    centred hint + "Open page" button        next / prev / close,
 *                  (the target never appeared)              "Open page" -> loading(i)
 *   ended          nothing in the DOM, listeners removed    -
 *
 * Failure modes: the target never appears (fallback), a destination URL cannot
 * be worked out (`to` returns null: fallback without the button), browser Back
 * mid-tour (`notifyPop` ends it), the owner of the tour unmounts (`stop`).
 * Every pending timer / MutationObserver belongs to a `seq`; moving on, closing
 * or ending invalidates it, so nothing fires late and nothing is registered twice.
 *
 * Steps on the page the tour is already on behave exactly as before this
 * runtime existed: same overlay, same 450 ms after `beforeShow`, same 300 ms
 * popover delay, and a missing target is a centred hint at once.
 */
import type { TourDemoType } from './types'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourOverlay,
    createTourPopover,
    positionPopover,
} from './api'
import { shellText } from '../i18n'

/** What a `to` resolver can read. URLs are `pathname + search`. */
export interface GuidedTourContext {
    /** The URL now. */
    current: string
    /** `visited[i]`: the URL step i was last shown at (undefined: not shown yet). */
    visited: (string | undefined)[]
}

export interface GuidedTourStep {
    selector: string
    /**
     * Highlighted instead when `selector` never shows up: for a target that
     * depends on data (a button that only exists at one stage of a record).
     * Only used on a step that waits for its page (`path` / `to`); it is taken
     * once it has been visible for a short grace period without `selector`.
     */
    fallbackSelector?: string
    title: string
    content: string
    position?: 'top' | 'right' | 'bottom' | 'left'
    /** Demo animation type from tour-styles. */
    demo?: TourDemoType
    /**
     * Prepare the page for this step (e.g. open the popover that holds the
     * target). The step is drawn ~450 ms later, once the target has mounted.
     * Runs again on every visit of the step.
     */
    beforeShow?: () => void
    /**
     * Route this step's target lives on, e.g. `/customers` or `/customers/:id`
     * (a `:param` segment matches any one segment). Moving to a step whose
     * path does not match the current page navigates there (a path with no
     * `:param` is its own destination).
     */
    path?: string
    /**
     * Where to go when entering this step: a URL, or a function computing it
     * from the page the tour is on (`/customers/:id` needs a record: read the
     * first row's link off the list). Return null when it cannot be worked
     * out. Going back to a step already shown returns to the URL it was shown at.
     */
    to?: string | ((ctx: GuidedTourContext) => string | null)
}

export interface GuidedTourConfig {
    steps: GuidedTourStep[]
    /** Router navigation (push). Never `window.location`. */
    navigate: (url: string) => void
    /** The current `pathname + search`. */
    getUrl: () => string
    /** Runs once when the tour ends, however it ends. */
    onEnd?: () => void
}

export interface GuidedTour {
    /** End the tour now (owner unmounted, a new tour started). */
    stop: () => void
    /** Browser Back / Forward happened: ends the tour. */
    notifyPop: () => void
    readonly active: boolean
}

/** How long to wait for a target after changing page, in ms. */
export const TARGET_TIMEOUT_MS = 8000

const SETTLE_AFTER_BEFORE_SHOW_MS = 450
/** `fallbackSelector` is taken this long after it appeared, if `selector` has not. */
const FALLBACK_GRACE_MS = 1500
const POPOVER_DELAY_MS = 300

const pathMatches = (pattern: string, pathname: string): boolean => {
    const a = pattern.split('/').filter(Boolean)
    const b = pathname.split('/').filter(Boolean)
    return (
        a.length === b.length &&
        a.every((seg, i) => seg.startsWith(':') || seg === b[i])
    )
}

const isVisible = (el: Element | null): el is HTMLElement => {
    if (!el) return false
    const r = el.getBoundingClientRect()
    return r.width > 0 && r.height > 0
}

const findTarget = (selector: string): HTMLElement | null =>
    document.querySelector<HTMLElement>(selector)

const pathnameOf = (url: string): string => url.split(/[?#]/)[0]

type PopoverExtra = {
    loading?: boolean
    action?: { label: string; onClick: () => void }
}

export function startGuidedTour(config: GuidedTourConfig): GuidedTour {
    const { steps, navigate, getUrl, onEnd } = config
    const visited: (string | undefined)[] = []
    let seq = 0
    let ended = false
    const disposers = new Set<() => void>()

    const disposePending = () => {
        disposers.forEach((d) => d())
        disposers.clear()
    }

    const later = (fn: () => void, ms: number, mySeq: number) => {
        const cancel = () => window.clearTimeout(id)
        const id = window.setTimeout(() => {
            disposers.delete(cancel)
            if (!ended && mySeq === seq) fn()
        }, ms)
        disposers.add(cancel)
    }

    const end = () => {
        if (ended) return
        ended = true
        seq++
        disposePending()
        cleanupTourElements()
        onEnd?.()
    }

    /** Calls `done` with a visible `selector` element, or null after the timeout. */
    const waitForTarget = (
        selector: string,
        fallbackSelector: string | undefined,
        mySeq: number,
        done: (el: HTMLElement | null) => void
    ) => {
        let finished = false
        let grace = 0
        const stop = () => {
            observer.disconnect()
            window.clearTimeout(timeout)
            window.clearTimeout(grace)
            window.clearInterval(poll)
        }
        const cancel = () => {
            finished = true
            stop()
        }
        const finish = (el: HTMLElement | null) => {
            if (finished) return
            finished = true
            stop()
            disposers.delete(cancel)
            if (!ended && mySeq === seq) done(el)
        }
        const check = () => {
            const el = findTarget(selector)
            if (isVisible(el)) return finish(el)
            if (!fallbackSelector || grace) return
            if (isVisible(findTarget(fallbackSelector)))
                grace = window.setTimeout(() => {
                    const main = findTarget(selector)
                    const alt = findTarget(fallbackSelector)
                    finish(isVisible(main) ? main : isVisible(alt) ? alt : null)
                }, FALLBACK_GRACE_MS)
        }
        const observer = new MutationObserver(check)
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
        })
        // layout can make a node visible without a DOM mutation
        const poll = window.setInterval(check, 500)
        const timeout = window.setTimeout(() => finish(null), TARGET_TIMEOUT_MS)
        disposers.add(cancel)
        check()
    }

    /** Where step `index` lives: its URL (if known) and whether we are elsewhere. */
    const destinationOf = (
        index: number
    ): { url: string | null; needsNav: boolean } => {
        const step = steps[index]
        const current = getUrl()
        let url: string | null = null
        if (visited[index] !== undefined) url = visited[index]!
        else if (typeof step.to === 'function')
            url = step.to({ current, visited: [...visited] })
        else if (typeof step.to === 'string') url = step.to
        else if (step.path && !step.path.includes(':')) url = step.path
        if (url) return { url, needsNav: url !== current }
        // no URL: is the page at least the right one?
        const pageOk = !step.path || pathMatches(step.path, pathnameOf(current))
        return { url: null, needsNav: !pageOk }
    }

    const centre = (popover: HTMLElement) => {
        popover.style.position = 'fixed'
        popover.style.top = '50%'
        popover.style.left = '50%'
        popover.style.transform = 'translate(-50%, -50%)'
    }

    const popoverFor = (index: number, extra: PopoverExtra = {}) => {
        const step = steps[index]
        const last = index === steps.length - 1
        return createTourPopover({
            title: step.title,
            description: step.content,
            currentStep: index + 1,
            totalSteps: steps.length,
            onPrev: index > 0 ? () => show(index - 1) : undefined,
            onNext: extra.loading
                ? () => {}
                : last
                  ? end
                  : () => show(index + 1),
            onClose: end,
            isLastStep: last,
            demo: extra.loading ? undefined : step.demo,
            ...extra,
        })
    }

    const renderStep = (
        index: number,
        target: HTMLElement | null,
        mySeq: number
    ) => {
        cleanupTourElements()
        createTourOverlay()
        if (target) {
            target.classList.add('tour-highlight-element')
            target.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
        visited[index] = getUrl()
        later(
            () => {
                const popover = popoverFor(index)
                document.body.appendChild(popover)
                if (target) positionPopover(popover, target)
                else centre(popover)
            },
            target ? POPOVER_DELAY_MS : 100,
            mySeq
        )
    }

    /** Target never appeared: centred hint, with "Open page" when the page is known. */
    const renderFallback = (index: number, dest: string | null) => {
        cleanupTourElements()
        createTourOverlay()
        const popover = popoverFor(index, {
            action: dest
                ? {
                      label: shellText('tour_nav.open_page'),
                      onClick: () => show(index, true),
                  }
                : undefined,
        })
        document.body.appendChild(popover)
        centre(popover)
    }

    const renderLoading = (index: number) => {
        cleanupTourElements()
        createTourOverlay()
        const popover = popoverFor(index, { loading: true })
        // the title slot says what is going on
        const h3 = popover.querySelector('h3')
        if (h3) h3.textContent = shellText('tour_nav.loading')
        document.body.appendChild(popover)
        centre(popover)
    }

    /** Show step `index`, going to its page first when it is not there. */
    function show(index: number, retry = false): void {
        const step = steps[index]
        if (ended || !step) return
        const mySeq = ++seq
        disposePending()
        injectTourStyles()

        const { url, needsNav } = destinationOf(index)

        if (!needsNav && !retry) {
            // Same page: behaviour unchanged (see the file header).
            cleanupTourElements()
            createTourOverlay()
            const draw = () =>
                renderStep(index, findTarget(step.selector), mySeq)
            if (step.beforeShow) {
                step.beforeShow()
                later(draw, SETTLE_AFTER_BEFORE_SHOW_MS, mySeq)
            } else draw()
            return
        }

        if (needsNav && !url) {
            // We cannot work out where this step lives.
            renderFallback(index, null)
            return
        }
        renderLoading(index)
        if (needsNav && url) navigate(url)
        // let the router render the page, then prepare it and wait for the target
        later(
            () => {
                step.beforeShow?.()
                waitForTarget(
                    step.selector,
                    step.fallbackSelector,
                    mySeq,
                    (el) => {
                        if (el) renderStep(index, el, mySeq)
                        else renderFallback(index, url ?? getUrl())
                    }
                )
            },
            0,
            mySeq
        )
    }

    show(0)

    return {
        stop: end,
        notifyPop: end,
        get active() {
            return !ended
        },
    }
}
